import { FC } from "react";

import {
  Box,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Portal,
  IconButton,
  Radio,
  RadioGroup,
  SettingsIcon,
  useDisclosure,
} from "@hightouch/htui";
import noop from "lodash/noop";

import { Mapping } from "./types";

type Props = {
  isDisabled?: boolean;
  setValue?: (value) => void;
  supportOnlyWriteIfNull?: boolean;
  value?: Mapping & { ignoreNull?: boolean; onlyWriteIfNull?: boolean };
};

type Option = {
  description: string;
  label: string;
  value: string;
};

const options: Option[] = [
  {
    description:
      "This will sync null values. Caution as this can erase production data. Does not apply to nested object or array properties.",
    label: "Always sync",
    value: "always",
  },
  {
    description:
      "This will avoid syncing any null values from your source to destination.",
    label: "Don’t sync null values",
    value: "ignoreNull",
  },
  {
    description:
      "This will only write this field if the destination value is currently empty. Existing non-empty values will not be overwritten.",
    label: "Only write to empty fields",
    value: "onlyWriteIfNull",
  },
];

function getSelectedOption(value?: {
  ignoreNull?: boolean;
  onlyWriteIfNull?: boolean;
}): string {
  if (value?.onlyWriteIfNull) return "onlyWriteIfNull";
  if (value?.ignoreNull) return "ignoreNull";
  return "always";
}

export const SyncType: FC<Readonly<Props>> = ({
  isDisabled = false,
  setValue = noop,
  supportOnlyWriteIfNull = false,
  value = { ignoreNull: undefined },
}) => {
  const { isOpen, onClose, onToggle } = useDisclosure();
  const selected = getSelectedOption(value);
  const hasNonDefault = selected !== "always";
  const visibleOptions = supportOnlyWriteIfNull
    ? options
    : options.filter((o) => o.value !== "onlyWriteIfNull");
  return (
    <Popover isLazy isOpen={isOpen} onClose={onClose} placement="bottom-start">
      <Box pos="relative">
        <PopoverTrigger>
          <IconButton
            aria-label="Select sync type"
            icon={SettingsIcon}
            isDisabled={isDisabled}
            onClick={onToggle}
          />
        </PopoverTrigger>
        {hasNonDefault && (
          <Box position="absolute" right={0} top={1} zIndex={1}>
            <Box
              background="primary.base"
              borderRadius="full"
              height={2}
              width={2}
            />
          </Box>
        )}
      </Box>
      <Portal>
        <PopoverContent p={2} width="320px">
          <PopoverBody p={0}>
            <RadioGroup
              onChange={(option) => {
                setValue({
                  ...value,
                  ignoreNull: option === "ignoreNull" ? true : undefined,
                  onlyWriteIfNull:
                    option === "onlyWriteIfNull" ? true : undefined,
                });
                onClose();
              }}
              value={selected}
            >
              {visibleOptions.map(({ description, label, value }, index) => (
                <Radio
                  key={index}
                  description={description}
                  label={label}
                  value={value}
                />
              ))}
            </RadioGroup>
          </PopoverBody>
        </PopoverContent>
      </Portal>
    </Popover>
  );
};
